import React, { useMemo } from "react";
import "./QuestionGridNumber.css";
import useAppHooks from "../../../../shared/hooks/useAppHooks";
import { getExamSectionKey } from "../../../../shared/utilities/helpers/examSectionKey";

export default function QuestionGridNumber({ selectedIndex, onSelect }) {
  const { selector } = useAppHooks();
  const { questionsData } = selector((s) => s.examSlice);
  const { practiceSolution } = selector((s) => s.analyticsSlice);

  const analyticsData = practiceSolution?.data || [];

  const data = questionsData.map((q) => {
    const analyticsQ = analyticsData.find(
      (a) => String(a.qid) === String(q.qid)
    );

    return {
      ...q,
      is_correct: analyticsQ?.is_correct,
      correct: analyticsQ?.correct,
    };
  });

  const hasAttemptedAnswer = (q) => {
    const hasOptionAnswer = Array.isArray(q.selectedOption)
      ? q.selectedOption.length > 0
      : q.selectedOption !== null &&
        q.selectedOption !== undefined &&
        q.selectedOption !== "";
    const hasIntegerAnswer =
      String(q.integerAnswer ?? q.integer_Answer ?? "").trim() !== "";
    return hasOptionAnswer || hasIntegerAnswer;
  };

  const isQuestionCorrect = (q) => {
    const answerType = Number(q.answer_type_id);
    if (answerType === 2 && Array.isArray(q.option)) {
      const selectedIds = Array.isArray(q.selectedOption)
        ? q.selectedOption.map((id) => String(id))
        : q.selectedOption !== null &&
          q.selectedOption !== undefined &&
          q.selectedOption !== ""
        ? [String(q.selectedOption)]
        : [];
      const correctIds = q.option
        .filter((opt) => Number(opt.is_correct) === 1)
        .map((opt) => String(opt.option_id));

      if (!selectedIds.length || !correctIds.length) return false;

      const selectedSet = new Set(selectedIds);
      const correctSet = new Set(correctIds);
      if (selectedSet.size !== correctSet.size) return false;
      return [...correctSet].every((id) => selectedSet.has(id));
    }

    if (answerType === 1 && Array.isArray(q.option)) {
      const selectedId = String(q.selectedOption ?? "");
      if (!selectedId) return false;
      return q.option.some(
        (opt) =>
          String(opt.option_id) === selectedId && Number(opt.is_correct) === 1
      );
    }

    if (answerType === 3) {
      const userAns = String(q.integerAnswer ?? q.integer_Answer ?? "").trim();
      const correctAns = String(q.answer ?? "").trim();
      if (!userAns || !correctAns) return false;
      const userNum = Number(userAns);
      const correctNum = Number(correctAns);
      if (Number.isFinite(userNum) && Number.isFinite(correctNum)) {
        return userNum === correctNum;
      }
      return userAns === correctAns;
    }

    if (q.is_correct !== undefined && q.is_correct !== null) {
      return Number(q.is_correct) === 1;
    }
    if (q.correct !== undefined && q.correct !== null) {
      const normalized = String(q.correct).trim().toLowerCase();
      return (
        normalized === "1" || normalized === "true" || normalized === "correct"
      );
    }
    return false;
  };

  // Group data by subject & section
  const groupedData = useMemo(() => {
    if (!data.length) return [];
    const sections = [];
    let currentSection = null;
    data.forEach((q) => {
      const key = getExamSectionKey(q) || "Unknown";
      if (
        !currentSection ||
        currentSection.key !== key ||
        q.display_number === 1
      ) {
        currentSection = {
          key,
          heading: getExamSectionKey(q) || "Unknown",
          questions: [],
        };
        sections.push(currentSection);
      }
      currentSection.questions.push(q);
    });
    return sections;
  }, [data]);


  const statusCounts = useMemo(() => {
  const counts = {
    correct: 0,
    incorrect: 0,
    unattempted: 0,
    marked: 0,
  };

  data.forEach((q) => {
    const attempted = hasAttemptedAnswer(q);

    if (q.marked) {
      counts.marked++;
    } else if (!attempted) {
      counts.unattempted++;
    } else {
      isQuestionCorrect(q) ? counts.correct++ : counts.incorrect++;
    }
  });

  return counts;
}, [data]);

  if (!data.length)
    return <p style={{ textAlign: "center" }}>No questions found</p>;

  let globalIndex = 0;

//   const getClass = (q, globalI) => {
//   const isSelected = globalI === selectedIndex;

//   if (isSelected) return "selected";
//   if (q.marked) return "marked";

//   const attempted =
//     (q.selectedOption !== null && q.selectedOption !== undefined) ||
//     (q.integerAnswer && String(q.integerAnswer).trim() !== "");

//   if (!attempted) return "unattempted";

//   let isCorrect = false;

//   if (q.option && Array.isArray(q.option)) {
//     q.option.forEach((opt) => {
//       if (
//         String(opt.option_id) === String(q.selectedOption) &&
//         opt.is_correct === 1
//       ) {
//         isCorrect = true;
//       }
//     });
//   }

//   return isCorrect ? "correct" : "incorrect";
// };

  const getClass = (q) => {
  if (q.marked) return "marked";

  const attempted = hasAttemptedAnswer(q);

  if (!attempted) return "unattempted";

  return isQuestionCorrect(q) ? "correct" : "incorrect";
};

  return (
    <div className="questionGrid_wrapper">
      <h4 className="grid_heading">Your Response Grid</h4>
       <h4 className="grid_overview">overview</h4>

      {/* Status Legend with Counts Inside Circles */}
      <div className="overview">
        <div className="status_row">
          <span className="status_circle correct">{statusCounts.correct}</span>
          Correct
        </div>
        <div className="status_row">
          <span className="status_circle incorrect">{statusCounts.incorrect}</span>
          Incorrect
        </div>
        <div className="status_row">
          <span className="status_circle unattempted">{statusCounts.unattempted}</span>
          Unattempted
        </div>
        <div className="status_row">
          <span className="status_circle marked">{statusCounts.marked}</span>
          Marked for Review
        </div>
      </div>

      {/* Sections and Questions */}
      {groupedData.map((section, sectionIndex) => (
        <div key={sectionIndex} className="section_wrapper">
          <h5>{section.heading}</h5>
          <div className="grid">
            {section.questions.map((q) => {
              
              const index = globalIndex++;
              const className = getClass(q, index);

              const questionNumber =index+1; 
              return (
                <div
                  key={index}
                  className={`grid_item ${className}`}
                  onClick={() => onSelect(index)}
                  title={`Q${questionNumber}`}
                >
                  {questionNumber}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}